/**
 * Chunk-load failure handling (#766).
 *
 * After a deploy, a browser holding the previous build's HTML asks for JS
 * chunks whose hashed filenames no longer exist on the new server, and
 * webpack raises `ChunkLoadError` / "Loading chunk N failed". That is not an
 * application bug — the fix is to reload so the client picks up the current
 * build. The root fix (serving old assets from a CDN via assetPrefix) is
 * infra; until then we reload once, guarded so a chunk that is genuinely
 * broken cannot put the browser into a reload loop.
 *
 * Pure logic lives here (apps/web's vitest runs without jsdom); the DOM
 * wiring is in components/system/GlobalErrorListener.tsx and the error
 * boundaries.
 */

const CHUNK_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk \S+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
] as const

export function isChunkLoadError(input: unknown): boolean {
  if (input === null || input === undefined) return false
  const name =
    typeof input === 'object' && typeof (input as { name?: unknown }).name === 'string'
      ? (input as { name: string }).name
      : ''
  const message =
    typeof input === 'string'
      ? input
      : typeof input === 'object' && typeof (input as { message?: unknown }).message === 'string'
        ? (input as { message: string }).message
        : ''
  if (name === 'ChunkLoadError') return true
  return CHUNK_PATTERNS.some((re) => re.test(message))
}

export const CHUNK_RELOAD_STORAGE_KEY = 'pc:chunk-reload-at'

/** Reloads closer together than this are treated as a loop and refused. */
export const CHUNK_RELOAD_COOLDOWN_MS = 60_000

/** The subset of `Storage` we need — lets tests pass a plain in-memory map. */
export type StorageLike = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * Decide whether to reload now, and record the attempt. Exactly one reload
 * per cooldown window: a second chunk failure inside the window means the
 * reload did not help (or the build really is broken), so we fall through to
 * the normal error UI instead of spinning.
 */
export function shouldReloadForChunkError(
  storage: StorageLike | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!storage) return false
  try {
    const raw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY)
    const last = raw ? Number(raw) : NaN
    if (Number.isFinite(last) && now - last < CHUNK_RELOAD_COOLDOWN_MS) {
      return false
    }
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now))
    return true
  } catch {
    // Storage blocked (Safari private mode, quota) — we cannot guard, so we
    // must not reload: an unguarded reload could loop forever.
    return false
  }
}

/**
 * Browser entry point: if `err` is a chunk-load failure, perform one guarded
 * reload. Returns true when a reload was triggered (callers should then skip
 * reporting the error and rendering an error UI).
 */
export function handleChunkLoadError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false
  if (typeof window === 'undefined') return false
  let storage: StorageLike | null = null
  try {
    storage = window.sessionStorage
  } catch {
    storage = null
  }
  if (!shouldReloadForChunkError(storage)) return false
  window.location.reload()
  return true
}
